import type { CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'
import type { MatchPhase, Team } from '@brochure/shared'
import type { BracketRound, BracketSlot } from '@/lib/bracket'
import { phaseKey, phaseParams } from '@/lib/stoccopong-format'
import { LocalizedLink } from '@/components/LocalizedLink'
import { Stamp } from '@/components/ui/Stamp'
import { SideMark } from './SideMark'
import { cn } from '@/lib/utils'
import { ScrollingName } from '@/components/ui/ScrollingName'

type BracketProps = { rounds: BracketRound[]; teams: Team[]; tournamentId: string }

/** Un lato della card: nome della squadra, oppure il segnaposto (piazzamento, vincente o perdente di). */
type Side = { label: string; known: boolean }

const LABEL = 'font-body text-xs font-semibold uppercase tracking-widest text-ink-soft'

function BracketCard({ slot, sides, code, accent, tournamentId }: { slot: BracketSlot; sides: [Side, Side]; code?: string; accent?: boolean; tournamentId: string }) {
  const { t } = useTranslation()
  const match = slot.match
  const hasScore = !!match && match.homeScore !== null && match.awayScore !== null
  const scores = [match?.homeScore, match?.awayScore]
  const isLive = match?.status === 'live'
  const clickable = !!match && (match.status === 'live' || match.status === 'completed')

  const card = (
    <div
      className={cn(
        'flex min-h-[4.75rem] w-full flex-col justify-center gap-1.5 rounded-xl border px-3.5 py-2.5',
        accent ? 'border-2 border-sun bg-paper shadow-[3px_3px_0_var(--color-sun)]' : isLive ? 'border-coral bg-paper' : 'border-line bg-paper-deep/60',
      )}
    >
      {(code || isLive) && (
        <div className="flex items-center gap-2 font-body text-[0.6rem] font-semibold uppercase tracking-widest">
          {code && <span className="text-ink-soft">{code}</span>}
          {isLive && (
            <span className="flex items-center gap-1 text-coral-ink">
              <span className="size-1.5 rounded-full bg-coral motion-safe:animate-pulse" aria-hidden="true" />
              {t('stoccoPong.live')}
            </span>
          )}
        </div>
      )}
      {sides.map((side, i) => (
        <div key={i} className="flex items-center justify-between gap-2">
          <SideMark side={i === 0 ? 'home' : 'away'} />
          <ScrollingName className={cn('flex-1 font-body text-sm', side.known ? 'text-ink' : 'text-ink-soft')}>{side.label}</ScrollingName>
          <span className="shrink-0 font-display tabular-nums text-ink">{hasScore ? scores[i] : ''}</span>
        </div>
      ))}
    </div>
  )

  if (clickable) {
    return (
      <LocalizedLink to="stoccoPongMatch" params={{ id: tournamentId, matchId: match!.id }} data-testid={`match-${match!.id}`} className="block w-full transition-transform duration-200 ease-out-expo active:scale-[0.99] lg:hover:-translate-y-0.5">
        {card}
      </LocalizedLink>
    )
  }
  return (
    <div className="w-full" data-testid={match ? `match-${match.id}` : undefined}>
      {card}
    </div>
  )
}

/** Linee del tabellone (solo da `lg`): verso il turno dopo a destra, dal turno prima a sinistra. Lunghe metà del `gap-x-12`. */
function Connectors({ toNext, fromPrev, isTop }: { toNext: boolean; fromPrev: boolean; isTop: boolean }) {
  const line = 'absolute hidden bg-ink/25 lg:block'
  return (
    <>
      {toNext && <span aria-hidden="true" className={cn(line, 'top-1/2 -right-6 h-px w-6')} />}
      {toNext && <span aria-hidden="true" className={cn(line, '-right-6 w-px', isTop ? 'top-1/2 bottom-0' : 'top-0 bottom-1/2')} />}
      {fromPrev && <span aria-hidden="true" className={cn(line, 'top-1/2 -left-6 h-px w-6')} />}
    </>
  )
}

/**
 * Tabellone. Telefono: turni impilati a tutta larghezza. Da `lg`: una griglia unica con un turno per colonna,
 * i titoli sulla prima riga e ogni partita centrata tra le due da cui arriva (occupa 2^turno righe), con le linee
 * di collegamento; la finalina sotto la finale. Le card sono le stesse nei due layout: da `lg` i wrapper sono `contents`.
 */
export function Bracket({ rounds, teams, tournamentId }: BracketProps) {
  const { t } = useTranslation()
  if (rounds.length === 0) return null

  const nameOf = (teamId: string | null) => teams.find((team) => team.id === teamId)?.name
  const side = (teamId: string | null, fallback: string): Side => {
    const name = nameOf(teamId)
    return name ? { label: name, known: true } : { label: fallback, known: false }
  }
  // Sigla della partita ("Q1", "S2"): i segnaposto "Vincente Q1" rimandano a lei.
  const code = (phase: MatchPhase, index: number) => `${t(`stoccoPong.bracket.code.${phaseKey(phase).replace('phases.', '')}`, phaseParams(phase))}${index + 1}`
  const placeholder = (roundIndex: number, slotIndex: number, which: 0 | 1, seed?: number) => {
    if (seed) return t('stoccoPong.bracket.seed', { n: seed })
    if (roundIndex === 0) return t('stoccoPong.bracket.tbd')
    return t('stoccoPong.bracket.winnerOf', { code: code(rounds[roundIndex - 1].phase, slotIndex * 2 + which) })
  }

  const finalRound = rounds[rounds.length - 1]
  const finalMatch = finalRound.matches[0]?.match
  const finalDecided = finalMatch?.status === 'completed' && finalMatch.homeScore !== null && finalMatch.awayScore !== null
  const championId = finalDecided ? (finalMatch!.homeScore! > finalMatch!.awayScore! ? finalMatch!.homeTeamId : finalMatch!.awayTeamId) : null
  const runnerUpId = championId ? (championId === finalMatch!.homeTeamId ? finalMatch!.awayTeamId : finalMatch!.homeTeamId) : null

  const thirdPlace = finalRound.thirdPlace
  const thirdMatch = thirdPlace?.match
  const thirdDecided = thirdMatch?.status === 'completed' && thirdMatch.homeScore !== null && thirdMatch.awayScore !== null
  const thirdPlaceId = thirdDecided ? (thirdMatch!.homeScore! > thirdMatch!.awayScore! ? thirdMatch!.homeTeamId : thirdMatch!.awayTeamId) : null
  // `thirdPlace` c'è solo con almeno due turni: il penultimo è la semifinale che la alimenta.
  const semifinal = rounds[rounds.length - 2]
  const thirdSides = semifinal && thirdPlace
    ? ([side(thirdPlace.homeTeamId, t('stoccoPong.bracket.loserOf', { code: code(semifinal.phase, 0) })), side(thirdPlace.awayTeamId, t('stoccoPong.bracket.loserOf', { code: code(semifinal.phase, 1) }))] as [Side, Side])
    : null

  // Righe: titoli, una per partita del primo turno, poi titolo e card della finalina.
  const firstCount = rounds[0].matches.length
  const grid = {
    '--cols': `repeat(${rounds.length}, minmax(12rem, 1fr))`,
    '--rows': `auto repeat(${firstCount}, minmax(6rem, auto))${thirdSides ? ' auto auto' : ''}`,
  } as CSSProperties

  return (
    <div>
      <div className="grid grid-cols-1 gap-7 lg:grid-cols-(--cols) lg:grid-rows-(--rows) lg:gap-x-12 lg:gap-y-0" style={grid}>
        {rounds.map((round, r) => {
          const span = 2 ** r
          const isFinal = r === rounds.length - 1
          return (
            <div key={round.phase} className="flex flex-col gap-3 lg:contents" style={{ '--col': r + 1 } as CSSProperties}>
              <p className={cn(LABEL, 'lg:col-start-(--col) lg:row-start-1 lg:pb-1')}>{t(phaseKey(round.phase), phaseParams(round.phase))}</p>
              <div className="grid grid-cols-1 gap-2.5 lg:contents">
                {round.matches.map((slot, i) => (
                  <div
                    key={slot.match?.id ?? `${round.phase}-${i}`}
                    className="relative lg:col-start-(--col) lg:row-span-(--span) lg:row-start-(--row) lg:flex lg:items-center lg:py-2"
                    style={{ '--row': 2 + i * span, '--span': span } as CSSProperties}
                  >
                    <Connectors toNext={!isFinal} fromPrev={r > 0} isTop={i % 2 === 0} />
                    <BracketCard
                      slot={slot}
                      sides={[side(slot.homeTeamId, placeholder(r, i, 0, slot.homeSeed)), side(slot.awayTeamId, placeholder(r, i, 1, slot.awaySeed))]}
                      code={round.matches.length > 1 ? code(round.phase, i) : undefined}
                      accent={round.phase === 'final'}
                      tournamentId={tournamentId}
                    />
                  </div>
                ))}
              </div>
              {isFinal && thirdPlace && thirdSides && (
                <div className="mt-4 flex flex-col gap-3 lg:contents" style={{ '--row': firstCount + 2 } as CSSProperties}>
                  <p className={cn(LABEL, 'lg:col-start-(--col) lg:row-start-(--row) lg:pt-6 lg:pb-1')}>{t(phaseKey('third_place'))}</p>
                  <div className="lg:col-start-(--col) lg:row-start-(--next) lg:py-2" style={{ '--next': firstCount + 3 } as CSSProperties}>
                    <BracketCard slot={thirdPlace} sides={thirdSides} tournamentId={tournamentId} />
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>

      {championId && (
        <div className="mt-10 flex flex-col items-center gap-2 text-center">
          <Stamp tone="sun">{t('stoccoPong.bracket.champions')}</Stamp>
          <p className="font-display text-3xl text-ink">{nameOf(championId)}</p>
          {runnerUpId && (
            <p className="font-body text-sm text-ink-soft">
              {t('stoccoPong.bracket.second')} {nameOf(runnerUpId)}
            </p>
          )}
          {thirdPlaceId && (
            <p className="font-body text-sm text-ink-soft">
              {t('stoccoPong.bracket.third')} {nameOf(thirdPlaceId)}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
